/**
 * Dashboard — post-login shell. Hover-expand glass sidebar + sticky glass
 * header, ported from the Thema Console design. Two pages: Analytics and Vault
 * Control. The vault config is resolved once; <VaultWorkspace> owns the
 * on-chain hooks so both pages share one data set.
 */

import { useEffect, useState } from "react";
import { useWallet, useWalletContext } from "@thema/ui/wallet";
import { useVaultConfig } from "@thema/ui";
import { useAuth } from "../contexts/AuthContext";
import { CONSOLE_DEPLOYMENT_ID, CONSOLE_VAULT_REF } from "../console-config";
import { Logo } from "./Brand";
import { Icons } from "./icons";
import { Callout } from "./primitives";
import VaultWorkspace from "./VaultWorkspace";

export type ConsoleTab = "analytics" | "vault";

const NAV_ITEMS: { id: ConsoleTab; icon: keyof typeof Icons; label: string }[] = [
  { id: "analytics", icon: "home", label: "Analytics" },
  { id: "vault", icon: "vault", label: "Vault" },
];

function Sidebar({
  page,
  navigate,
  expanded,
  setExpanded,
  onSignOut,
}: {
  page: ConsoleTab;
  navigate: (p: ConsoleTab) => void;
  expanded: boolean;
  setExpanded: (v: boolean) => void;
  onSignOut: () => void;
}) {
  return (
    <aside
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 60,
        width: expanded ? 232 : 72,
        transition: "width 0.28s var(--smooth)",
        background: "var(--glass-strong)",
        backdropFilter: "blur(24px) saturate(1.4)",
        WebkitBackdropFilter: "blur(24px) saturate(1.4)",
        borderRight: "1px solid var(--border)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div style={{ height: 70, display: "flex", alignItems: "center", padding: expanded ? "0 22px" : 0, justifyContent: expanded ? "flex-start" : "center", flex: "none" }}>
        <Logo size={24} markOnly={!expanded} />
      </div>
      <nav style={{ flex: 1, padding: "8px 12px", display: "flex", flexDirection: "column", gap: 4 }}>
        {NAV_ITEMS.map((it) => {
          const Ico = Icons[it.icon];
          const active = page === it.id;
          return (
            <button
              key={it.id}
              onClick={() => navigate(it.id)}
              title={it.label}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 13,
                padding: expanded ? "11px 12px" : "11px 0",
                justifyContent: expanded ? "flex-start" : "center",
                borderRadius: 11,
                cursor: "pointer",
                border: "none",
                background: active ? "var(--accent-soft)" : "transparent",
                color: active ? "var(--accent)" : "var(--text-soft)",
                transition: "all 0.16s var(--smooth)",
                position: "relative",
              }}
              onMouseEnter={(e) => {
                if (!active) e.currentTarget.style.background = "var(--surface-alt)";
              }}
              onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.background = "transparent";
              }}
            >
              {active && <span style={{ position: "absolute", left: -12, top: "50%", transform: "translateY(-50%)", width: 3, height: 20, borderRadius: 3, background: "var(--accent)" }} />}
              <Ico size={20} style={{ flex: "none" }} />
              {expanded && <span style={{ fontSize: 14, fontWeight: active ? 600 : 500, whiteSpace: "nowrap" }}>{it.label}</span>}
            </button>
          );
        })}
      </nav>
      <div style={{ padding: 12, flex: "none", display: "flex", flexDirection: "column", gap: 8 }}>
        <button
          onClick={onSignOut}
          title="Sign out"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 13,
            padding: expanded ? "11px 12px" : "11px 0",
            justifyContent: expanded ? "flex-start" : "center",
            borderRadius: 11,
            cursor: "pointer",
            border: "none",
            background: "transparent",
            color: "var(--text-soft)",
            transition: "all 0.16s var(--smooth)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "var(--surface-alt)";
            e.currentTarget.style.color = "var(--neg)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "var(--text-soft)";
          }}
        >
          <Icons.logout size={20} style={{ flex: "none" }} />
          {expanded && <span style={{ fontSize: 14, fontWeight: 500, whiteSpace: "nowrap" }}>Sign out</span>}
        </button>
        <div className="glass" style={{ borderRadius: 12, padding: expanded ? 14 : 10, display: "flex", alignItems: "center", gap: 10, justifyContent: expanded ? "flex-start" : "center" }}>
          <span className="dot" style={{ flex: "none" }} />
          {expanded && (
            <div>
              <div className="mono" style={{ fontSize: 10.5, color: "var(--green)", letterSpacing: "0.08em" }}>ENGINE ONLINE</div>
              <div className="mono" style={{ fontSize: 9.5, color: "var(--micro)" }}>v0.1 · console</div>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}

function Header({
  chainName,
  shortAddress,
  onConnect,
  onDisconnect,
  wrongChain,
}: {
  chainName: string;
  shortAddress: string | null | undefined;
  onConnect: () => void;
  onDisconnect: () => void;
  wrongChain: boolean;
}) {
  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 50,
        height: 70,
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "0 28px",
        background: "var(--glass)",
        backdropFilter: "blur(18px) saturate(1.3)",
        WebkitBackdropFilter: "blur(18px) saturate(1.3)",
        borderBottom: "1px solid var(--border)",
      }}
    >
      <div style={{ position: "relative", width: 280, maxWidth: "32vw" }}>
        <Icons.search size={16} style={{ position: "absolute", left: 13, top: 11, color: "var(--faint)" }} />
        <input className="input" style={{ height: 40, paddingLeft: 38, fontSize: 13.5, background: "var(--surface-alt)" }} placeholder="Search vaults, txns, addresses…" />
      </div>

      <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
        {/* chain chip */}
        <span className="chip" style={{ cursor: "default", background: "var(--surface)" }} title={wrongChain ? "Wallet on a different network" : undefined}>
          <span className="dot" style={{ background: wrongChain ? "var(--warn)" : "var(--green)" }} />
          <span style={{ fontWeight: 600 }}>{chainName}</span>
        </span>

        <button className="btn btn-ghost btn-md" style={{ width: 40, padding: 0 }}>
          <Icons.bell size={17} />
        </button>

        {/* wallet */}
        {shortAddress ? (
          <button className="chip" onClick={onDisconnect} style={{ background: "var(--surface)", paddingRight: 12 }} title="Click to disconnect">
            <span className="dot" />
            <span className="mono" style={{ fontSize: 12.5, fontWeight: 600 }}>{shortAddress}</span>
            <span className="badge" style={{ borderColor: "rgba(208,181,111,0.3)", background: "rgba(208,181,111,0.10)", color: "#9A7726", padding: "2px 6px" }}>SAFE</span>
          </button>
        ) : (
          <button className="btn btn-primary btn-md" onClick={onConnect}>
            <Icons.wallet size={16} /> Connect wallet
          </button>
        )}
      </div>
    </header>
  );
}

export default function Dashboard() {
  const [page, setPage] = useState<ConsoleTab>("analytics");
  const [expanded, setExpanded] = useState(false);
  const vault = useVaultConfig(CONSOLE_DEPLOYMENT_ID, CONSOLE_VAULT_REF);
  const { shortAddress, chainId } = useWallet();
  const { openModal, disconnect } = useWalletContext();
  const { logout } = useAuth();

  // Mark the page "entered" after intro animations so resting styles apply.
  useEffect(() => {
    const t = setTimeout(() => document.body.classList.add("entered"), 1400);
    return () => clearTimeout(t);
  }, []);

  const wrongChain = !!vault && chainId !== parseInt(vault.chainId, 10);

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)" }}>
      <div style={{ position: "fixed", inset: 0, zIndex: 0, background: "radial-gradient(60% 50% at 80% 0%, rgba(var(--accent-rgb),0.06), transparent 60%), radial-gradient(50% 40% at 0% 100%, rgba(61,231,208,0.05), transparent 60%)" }} />
      <Sidebar page={page} navigate={setPage} expanded={expanded} setExpanded={setExpanded} onSignOut={logout} />
      <div style={{ marginLeft: 72, position: "relative", zIndex: 1 }}>
        <Header
          chainName={vault?.chainName ?? "—"}
          shortAddress={shortAddress}
          onConnect={openModal}
          onDisconnect={disconnect}
          wrongChain={wrongChain}
        />
        <main style={{ padding: "30px clamp(20px,3.5vw,44px) 80px", maxWidth: 1360, margin: "0 auto", minHeight: "calc(100vh - 70px)" }}>
          {vault ? (
            <VaultWorkspace vault={vault} tab={page} />
          ) : (
            <div style={{ maxWidth: 540, margin: "60px auto 0" }}>
              <Callout tone="error">
                No vault is configured for deployment <span className="mono">{CONSOLE_DEPLOYMENT_ID}</span> (<span className="mono">{CONSOLE_VAULT_REF}</span>). Generate the SDK
                deployments or update <span className="mono">console-config.ts</span>.
              </Callout>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
